import React from "react";
import { EventContext } from "../../../common/contexts/event";
import { ThemeContext } from "../../../common/contexts/theme";
import { useSubscription } from "../../../common/hooks/useobservable";
import { format } from "../../../common/utilities/format";
import { clearUserCookies, getSignInUri, SessionContext } from "../../contexts/session";
import { IMeControl, IMeControlComponentOptions } from "./mecontroltypes";

import "./mecontrol.css";

export const meControlComponentContainerId = "me-control";
const baseSignOutUrl = "https://login.live.com/logout.srf?ct=1665689758&id=250206&ru={{redirectUrl}}%3Fmkt%3Den-US&mkt={{market}}";
const baseMeUrl = "https://login.live.com/me.srf?wa=wsignin1.0&wp=LBI&mkt={{market}}&wreply={{redirectUrl}}";

/**
 * Documentation for meControl
 * https://mecontroltestpartner.azurewebsites.net/PartnerDocs/index.html
 * @returns Element for the mecontrol
 */
export function MeControl(props: { meControl: IMeControl }): React.ReactElement {
  const { meControl } = props;

  const sessionContext = React.useContext(SessionContext);
  const themeContext = React.useContext(ThemeContext);
  const eventContext = React.useContext(EventContext);

  const pictureUrl = useSubscription(sessionContext.profileUrl, (pictureUrl) => {
    meControl.API.setProfilePicture(pictureUrl);
    return false;
  });

  const theme = useSubscription(themeContext.currentTheme, (theme) => {
    meControl.API.setTheme({ type: theme.dark ? "dark" : "light" });
    return false;
  });

  React.useEffect(() => {
    meControl.API.addEventListener("beforesignoutfromapp", clearUserCookies);
    meControl.API.addEventListener("beforeswitch", clearUserCookies);

    return () => {
      meControl.API.removeEventListener("beforesignoutfromapp", clearUserCookies);
      meControl.API.removeEventListener("beforeswitch", clearUserCookies);
    };
  }, [meControl.API]);

  React.useEffect(() => {
    const market = sessionContext.locale;
    const meUrl: string = format(baseMeUrl, { redirectUrl: window.location.origin, market });
    const signInUrl = getSignInUri(sessionContext, "me");
    const signOutUrl: string = format(baseSignOutUrl, { redirectUrl: window.location.origin, market });
    const switchParams = new URLSearchParams({ wreply: signInUrl });
    const switchToParams: { [parameterName: string]: string } = {};

    if (sessionContext.userProfile?.preferred_username) {
      switchToParams.username = sessionContext.userProfile.preferred_username;
    }

    const switchUrl = `${signOutUrl}&${switchParams.toString()}`;

    // Build configuration object
    const meControlOptions: IMeControlComponentOptions = {
      apiGeneration: "GEN2",
      containerId: meControlComponentContainerId,
      authProviderConfig: {
        type: "webMsaOnly",
        appSignInUrl: signInUrl,
        appSignInToUrl: signInUrl,
        appSignOutUrl: signOutUrl,
        appSwitchUrl: switchUrl,
        appSwitchToUrl: `${signInUrl}&${new URLSearchParams(switchToParams).toString()}`
      },
      msa: {
        signOutAndForgetUrl: signOutUrl,
        rememberedAccountsUrl: meUrl
      },
      currentAccount: undefined
    };

    // currentAccount should be undefined if the user is signed out
    if (sessionContext.userProfile && sessionContext.userId) {
      meControlOptions.currentAccount = {
        type: sessionContext.tenantType,
        authenticatedState: "signedIn",
        memberName: sessionContext.userProfile?.preferred_username || sessionContext.userProfile?.name,
        firstName: sessionContext.userProfile.given_name ?? "",
        lastName: sessionContext.userProfile.family_name ?? "",
        cid: sessionContext.userId,
        pictureUrl
      };
    }

    meControlOptions.primaryUXConfig = {
      theme: {
        type: theme.dark ? "dark" : "light"
      }
    };

    const loadSuccess: boolean = meControl.Loader.load(meControlOptions);
    if (!loadSuccess) {
      eventContext.dispatchEvent("telemetryAvailable", {
        action: "exception",
        error: "MeControl Options failed to load",
        name: "MeControlOptions"
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionContext, eventContext]);

  return <div id={meControlComponentContainerId} />;
}
